import axios, { AxiosInstance } from "axios";
import {
  HttpClient,
  HttpResponse,
  HttpRequestConfig,
} from "./http/HttpClientService";
import { withRetry, parseError, errorLogger } from "../utils/errors";

class ApiService implements HttpClient {
  private api: AxiosInstance;
  private isRefreshing: boolean = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor() {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || "/api";

    this.api = axios.create({
      baseURL: `${baseUrl}/v1`,
      timeout: 10000,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.api.interceptors.request.use(
      (config: any) => {
        const token = localStorage.getItem("token");
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest: any = error.config;
        if (error.response?.status === 401 && !originalRequest._retry && localStorage.getItem("token")) {
          if (this.isRefreshing) {
            return new Promise((resolve) => {
              this.refreshSubscribers.push((token: string) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(this.api(originalRequest));
              });
            });
          }
          originalRequest._retry = true;
          this.isRefreshing = true;
          const refreshed = await this.refreshToken();
          if (refreshed) {
            const newToken = localStorage.getItem("token");
            this.onTokenRefreshed(newToken!);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            this.isRefreshing = false;
            return this.api(originalRequest);
          }
          this.isRefreshing = false;
          this.refreshSubscribers = [];
          window.dispatchEvent(new CustomEvent("auth-token-expired"));
        }
        return Promise.reject(error);
      },
    );
  }

  private onTokenRefreshed(token: string): void {
    this.refreshSubscribers.forEach((callback) => callback(token));
    this.refreshSubscribers = [];
  }

  private async refreshToken(): Promise<boolean> {
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || "/api";
      const response = await axios.post(
        `${baseUrl}/v1/auth/refresh`,
        {},
        { withCredentials: true },
      );
      const newToken = response.data.access_token || response.data.token;
      if (newToken) {
        localStorage.setItem("token", newToken);
        return true;
      }
      return false;
    } catch (err) {
      console.error("Token refresh failed:", err);
      return false;
    }
  }

  getAxiosInstance(): AxiosInstance {
    return this.api;
  }

  private handleError(error: any) {
    const parsed = parseError(error);
    errorLogger.error(parsed);
    return {
      data: null as any,
      status: error.response?.status || 500,
      headers: {},
      error: parsed.userMessage,
    };
  }

  async get<T = any>(
    url: string,
    config?: HttpRequestConfig,
  ): Promise<HttpResponse<T>> {
    try {
      const response = await withRetry(() =>
        this.api.get(url, { params: config?.params, signal: config?.signal }),
      );
      // If response has both `data` and `pagination`, preserve the pagination field
      if (response.data?.data && response.data?.pagination) {
        return {
          data: response.data.data,
          pagination: response.data.pagination,
          status: response.status,
          headers: response.headers,
        };
      }
      return {
        data: response.data.data || response.data,
        status: response.status,
        headers: response.headers,
      };
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  async post<T = any>(
    url: string,
    data?: any,
    config?: HttpRequestConfig,
  ): Promise<HttpResponse<T>> {
    try {
      const response = await withRetry(() => this.api.post(url, data));
      return {
        data: response.data.data || response.data,
        status: response.status,
        headers: response.headers,
      };
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: HttpRequestConfig,
  ): Promise<HttpResponse<T>> {
    try {
      const response = await withRetry(() => this.api.put(url, data));
      return {
        data: response.data.data || response.data,
        status: response.status,
        headers: response.headers,
      };
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  async delete<T = any>(
    url: string,
    config?: HttpRequestConfig,
  ): Promise<HttpResponse<T>> {
    try {
      const response = await withRetry(() => this.api.delete(url));
      return {
        data: response.data?.data ?? response.data,
        status: response.status,
        headers: response.headers,
      };
    } catch (error: any) {
      return this.handleError(error);
    }
  }

  async login(email: string, password: string) {
    return this.post("/auth/login", { email, password });
  }

  async register(userData: any) {
    return this.post("/auth/register", userData);
  }

  async initiateGoogleOAuth(redirectUrl: string) {
    // This method initiates Google OAuth flow by redirecting to the backend endpoint
    // In practice, this would be used as: window.location.href = await this.initiateGoogleOAuth(redirectUrl)
    // But since it's a redirect, we'll return the URL
    return `/api/v1/auth/google?redirect=${encodeURIComponent(redirectUrl)}`;
  }

  async getUserProfile(userId: number) {
    return this.get(`/users/${userId}/profile`);
  }

  async updateUserProfile(userId: number, profileData: any) {
    return this.put(`/users/${userId}/profile`, profileData);
  }

  async changePassword(oldPassword: string, newPassword: string) {
    return this.post("/auth/change-password", {
      old_password: oldPassword,
      new_password: newPassword,
    });
  }

  async uploadAvatar(userId: number, avatarFile: File) {
    try {
      const formData = new FormData();
      formData.append("avatar", avatarFile);
      const response = await this.api.post(
        `/users/${userId}/avatar`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return {
        data: response.data.data || response.data,
        error: response.data?.error,
      };
    } catch (error: any) {
      return {
        data: null,
        error:
          error.response?.data?.message || error.message || "Upload failed",
      };
    }
  }

  async acceptTerms() {
    return this.post("/users/accept-terms", {});
  }

  async getTermsStatus() {
    return this.get("/users/terms-status");
  }

  async checkUserRequirements(): Promise<{ ok: boolean; status: number; missing?: string[] }> {
    try {
      const response = await this.api.get("/users/requirements");
      return { ok: true, status: response.status };
    } catch (error: any) {
      const data = error.response?.data || {};
      const metadata = data?.error?.metadata || data?.metadata || {};
      const missing = Array.isArray(metadata.missing) ? metadata.missing : [];
      return { ok: false, status: error.response?.status || 500, missing };
    }
  }

  async getStatusTimeouts() {
    return this.get("/settings/status-timeouts");
  }

  async getCommissionSettings() {
    return this.get("/settings/commissions");
  }

  async getUsers(params?: any, config?: HttpRequestConfig) {
    return this.get("/admin/users", { ...config, params });
  }

  async getUserSuggestions(query: string, limit?: number, excludeRole?: string) {
    const params: any = { q: query };
    if (limit) params.limit = limit;
    if (excludeRole) params.exclude_role = excludeRole;
    return this.get("/admin/users/suggestions", { params });
  }

  async createUser(userData: any) {
    return this.post("/admin/users", userData);
  }

  async getUser(userId: number) {
    return this.get(`/admin/users/${userId}`);
  }

  async updateUser(userId: number, userData: any) {
    return this.put(`/admin/users/${userId}`, userData);
  }

  async deleteUser(userId: number) {
    return this.delete(`/admin/users/${userId}`);
  }

  async banUser(userId: number, reason?: string) {
    return this.post(`/admin/users/${userId}/ban`, { reason });
  }

  async unbanUser(userId: number) {
    return this.post(`/admin/users/${userId}/unban`);
  }

  async getOrders(params?: any, config?: HttpRequestConfig) {
    return this.get("/orders", { ...config, params });
  }

  async getOrder(orderId: number, lang?: string) {
    return this.get(`/orders/${orderId}${lang ? `?lang=${lang}` : ""}`);
  }

  async createOrder(orderData: any) {
    return this.post("/orders", orderData);
  }

  async updateOrder(orderId: number, orderData: any) {
    return this.put(`/orders/${orderId}`, orderData);
  }

  async deleteOrder(orderId: number) {
    return this.delete(`/orders/${orderId}`);
  }

  async respondToOrder(orderId: number) {
    return this.put(`/orders/${orderId}/respond`);
  }

  async assignPerformer(orderId: number, performerId: number) {
    return this.put(`/orders/${orderId}/assign`, { performer_id: performerId });
  }

  async startOrder(orderId: number) {
    return this.put(`/orders/${orderId}/start`);
  }

  async completeOrder(orderId: number) {
    return this.put(`/orders/${orderId}/complete`);
  }

  async confirmOrder(orderId: number, rating?: number, review?: string) {
    return this.put(`/orders/${orderId}/confirm`, { rating, review });
  }

  async getQuery(query: string, params?: any) {
    return this.post("/query", { query, params });
  }

  async getQueryStats(model?: string, params?: any) {
    return this.get("/query/stats", {
      params: { ...(params || {}), ...(model ? { model } : {}) },
    });
  }

  async createCategoryLocalization(categoryId: number, localization: any) {
    return this.post(
      `/categories/${categoryId}/localization`,
      localization,
    );
  }

  async getAdminOrders(params?: any, config?: HttpRequestConfig) {
    return this.get("/admin/orders", { ...config, params });
  }

  async getAdminOrder(orderId: number) {
    return this.get(`/admin/orders/${orderId}`);
  }

  async updateOrderStatus(orderId: number, status: string, comment?: string) {
    return this.put(`/admin/orders/${orderId}/status`, { status, comment });
  }

  async resetOrderTimer(
    orderId: number,
    validityMinutes: number,
    reactivate?: boolean,
  ) {
    return this.post(`/admin/orders/${orderId}/reset-timer`, {
      validity_minutes: validityMinutes,
      reactivate,
    });
  }

  async getAdminStatistics(lang?: string) {
    return this.get("/admin/reports/statistics", { params: lang ? { lang } : {} });
  }

  async getAdminFinancialReport() {
    return this.get("/admin/reports/revenue");
  }

  async getAdminHeatmap(params?: any) {
    return this.get("/admin/heatmap-data", { params });
  }

  async getSystemSettings() {
    return this.get("/admin/settings");
  }

  async getSystemSettingById(id: number) {
    return this.get(`/admin/settings/${id}`);
  }

  async createSystemSetting(setting: any) {
    return this.post("/admin/settings", setting);
  }

  async updateSystemSetting(id: number, setting: any) {
    return this.put(`/admin/settings/${id}`, setting);
  }

  async deleteSystemSetting(id: number) {
    return this.delete(`/admin/settings/${id}`);
  }

  async getCategories(lang?: string) {
    return this.get("/categories", { params: lang ? { lang } : {} });
  }

  async createCategory(category: any) {
    return this.post("/categories", category);
  }

  async updateCategory(categoryId: number, category: any) {
    return this.put(`/categories/${categoryId}`, category);
  }

  async deleteCategory(categoryId: number) {
    return this.delete(`/categories/${categoryId}`);
  }

  async getAdminTransactions(params?: any) {
    return this.get("/admin/financial/transactions", { params });
  }

  async getAdminBalances(params?: any) {
    return this.get("/admin/financial/balances", { params });
  }

  async getAdminCommissions(params?: any) {
    return this.get("/admin/financial/commissions", { params });
  }

  async getAdminFines(params?: any) {
    return this.get("/admin/financial/fines", { params });
  }

  async getAdminFsmOverview() {
    return this.get("/admin/fsm/overview");
  }

  async getAdminFsmOrganizations(params?: any) {
    return this.get("/admin/fsm/organizations", { params });
  }

  async getAdminFsmOrganizationDetails(orgId: number) {
    return this.get(`/admin/fsm/organizations/${orgId}`);
  }

  async getAdminFsmOwners(params?: any) {
    return this.get("/admin/fsm/owners", { params });
  }

  async getAdminFsmOwner(ownerId: number) {
    return this.get(`/admin/fsm/owners/${ownerId}`);
  }

  async updateAdminFsmSubscription(subscriptionId: number, data: any) {
    return this.put(`/admin/fsm/subscriptions/${subscriptionId}`, data);
  }

  async markAdminFsmInvoicePaid(invoiceId: number) {
    return this.post(`/admin/fsm/invoices/${invoiceId}/mark-paid`);
  }

  async archiveBrigade(brigadeId: number) {
    return this.post(`/fsm/brigades/${brigadeId}/archive`);
  }

  async restoreBrigade(brigadeId: number) {
    return this.post(`/fsm/brigades/${brigadeId}/restore`);
  }

  async getAuditLogs(params?: any) {
    return this.get("/admin/audit", { params });
  }

  async getAuditLogById(id: number) {
    return this.get(`/admin/audit/${id}`);
  }

  async getReferralStatistics() {
    return this.get("/admin/referral/statistics");
  }

  async getReferralUsers(params?: any) {
    return this.get("/admin/referral/users", { params });
  }

  async getReferralBonuses(params?: any) {
    return this.get("/admin/referral/bonuses", { params });
  }

  async getAnalyticsUserGrowth(period?: string) {
    return this.get("/admin/analytics/users/growth", { params: { period } });
  }

  async getAnalyticsOrdersTrend(period?: string) {
    return this.get("/admin/analytics/orders/trend", { params: { period } });
  }

  async getAnalyticsRevenueDaily(period?: string) {
    return this.get("/admin/analytics/revenue/daily", { params: { period } });
  }

  async getAnalyticsRetention() {
    return this.get("/admin/analytics/retention");
  }

  async getConversionAnalytics(params?: any) {
    return this.get("/admin/analytics/conversion");
  }

  async getHeatmapData(params?: any, config?: HttpRequestConfig) {
    return this.get("/admin/heatmap-data", { ...config, params });
  }

  async getMapMarkers(params?: any, config?: HttpRequestConfig) {
    return this.get("/admin/map-markers", { ...config, params });
  }

  async getSearchSuggestions(query: string, lang?: string, limit?: number) {
    const params: any = { q: query };
    if (lang) params.lang = lang;
    if (limit) params.limit = limit;
    return this.get("/search/suggestions", { params });
  }

  async reverseGeocode(lat: number, lon: number) {
    return this.get("/geocode/reverse", { params: { lat, lon } });
  }

  async getOrderResponses(orderId: number) {
    return this.get(`/orders/${orderId}/responses`);
  }

  async applyReferralCode(userId: number, referralCode: string) {
    return this.post(`/users/${userId}/referral/apply`, {
      referral_code: referralCode,
    });
  }

  async uploadOrderPhoto(orderId: number, formData: FormData) {
    const response = await this.api.post(
      `/orders/${orderId}/upload-photo`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return {
      data: response.data.data || response.data,
      error: response.data?.error,
    };
  }

  async uploadDocument(userId: number, doc: File | FormData) {
    let formData: FormData;
    if (doc instanceof FormData) {
      formData = doc;
    } else {
      formData = new FormData();
      formData.append("document", doc);
    }
    const response = await this.api.post(`/users/${userId}/documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return {
      data: response.data.data || response.data,
      error: response.data?.error,
    };
  }

  async closeOrderWithCommission(orderId: number, data: any) {
    return this.put(`/orders/${orderId}/close-with-commission`, data);
  }

  async updatePerformerSettings(userId: number, settings: any) {
    return this.put(`/users/${userId}/performer-settings`, settings);
  }

  async getMyOrders(params?: any) {
    return this.get("/orders/my", { params });
  }

  async getNearbyOrders(lat: number, lng: number, radius?: number) {
    return this.get("/orders/nearby", { params: { lat, lng, radius } });
  }

  async rejectPerformer(orderId: number, performerId: number) {
    return this.put(`/orders/${orderId}/reject-performer`, {
      performer_id: performerId,
    });
  }

  async cancelOrder(orderId: number, reason?: string) {
    return this.put(`/orders/${orderId}/cancel`, { reason });
  }

  async extendOrder(orderId: number) {
    return this.put(`/orders/${orderId}/extend`);
  }

  async getOrderRemainingTime(orderId: number) {
    return this.get(`/orders/${orderId}/remaining-time`);
  }

  async getNotifications() {
    return this.get("/notifications");
  }

  async getUnreadNotifications() {
    return this.get("/notifications/unread");
  }

  async markNotificationAsRead(notificationId: number) {
    return this.put(`/notifications/${notificationId}/read`);
  }

  async markAllNotificationsAsRead() {
    return this.put("/notifications/read-all");
  }

  async getNotificationPreferences() {
    return this.get("/notifications/preferences");
  }

  async updateNotificationPreferences(preferences: any) {
    return this.put("/notifications/preferences", preferences);
  }

  async getUserBalance() {
    return this.get("/payments/balance");
  }

  async getBalanceHistory(params?: any) {
    return this.get("/payments/balance/history", { params });
  }

  async getUserTransactions(params?: any) {
    return this.get("/payments/transactions", { params });
  }

  async getPaymentCards() {
    return this.get("/payments/cards");
  }

  async addPaymentCard(cardData: any) {
    return this.post("/payments/cards", cardData);
  }

  async setPrimaryCard(cardId: number) {
    return this.put(`/payments/cards/${cardId}/set-primary`);
  }

  async deletePaymentCard(cardId: number) {
    return this.delete(`/payments/cards/${cardId}`);
  }

  async getPortfolio(userId: number) {
    return this.get(`/users/${userId}/portfolio`);
  }

  async addPortfolioItem(userId: number, item: any) {
    return this.post(`/users/${userId}/portfolio`, item);
  }

  async updatePortfolioItem(userId: number, itemId: number, item: any) {
    return this.put(`/users/${userId}/portfolio/${itemId}`, item);
  }

  async deletePortfolioItem(userId: number, itemId: number) {
    return this.delete(`/users/${userId}/portfolio/${itemId}`);
  }

  async uploadPortfolioImage(
    userId: number,
    file: File,
    metadata?: {
      title?: string;
      description?: string;
      category?: string;
      project_url?: string;
    },
  ) {
    try {
      const formData = new FormData();
      formData.append("image", file);
      if (metadata?.title) formData.append("title", metadata.title);
      if (metadata?.description)
        formData.append("description", metadata.description);
      if (metadata?.category) formData.append("category", metadata.category);
      if (metadata?.project_url)
        formData.append("project_url", metadata.project_url);
      const response = await this.api.post(
        `/users/${userId}/portfolio/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      return {
        data: response.data.data || response.data,
        error: response.data?.error,
      };
    } catch (error: any) {
      return {
        data: null,
        error:
          error.response?.data?.message || error.message || "Upload failed",
      };
    }
  }

  async updateSkills(userId: number, skills: string[]) {
    return this.put(`/users/${userId}/skills`, { skills });
  }

  async getCommissionsByOrder(orderId: number) {
    return this.get(`/payments/commissions/order/${orderId}`);
  }

  async getFinesByOrder(orderId: number) {
    return this.get(`/payments/fines/order/${orderId}`);
  }

  async getExpiredOrders() {
    return this.get("/admin/orders/expiration/expired");
  }

  async getFinancialSettings() {
    return this.get("/admin/settings/financial");
  }

  async getDeliverySettings() {
    return this.get("/admin/settings/delivery");
  }

  async getGeneralSettings() {
    return this.get("/admin/settings/general");
  }

  async getPaymentProviders() {
    return this.get("/admin/payment-providers");
  }

  async createPaymentProvider(data: any) {
    return this.post("/admin/payment-providers", data);
  }

  async updatePaymentProvider(id: number, data: any) {
    return this.put(`/admin/payment-providers/${id}`, data);
  }

  async deletePaymentProvider(id: number) {
    return this.delete(`/admin/payment-providers/${id}`);
  }

  async setDefaultPaymentProvider(id: number) {
    return this.put(`/admin/payment-providers/${id}/set-default`, {});
  }

  async testPaymentProvider(id: number) {
    return this.post(`/admin/payment-providers/${id}/test`, {});
  }

  // ---- FSM (Field Service Management) ----

  async listOrganizations() {
    return this.get("/fsm/organizations");
  }

  async createOrganization(data: any) {
    return this.post("/fsm/organizations", data);
  }

  async getOrganization(id: number) {
    return this.get(`/fsm/organizations/${id}`);
  }

  async updateOrganization(id: number, data: any) {
    return this.put(`/fsm/organizations/${id}`, data);
  }

  async uploadOrgLogo(id: number, file: File) {
    const formData = new FormData();
    formData.append("logo", file);
    return this.post(`/fsm/organizations/${id}/logo`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  async deleteOrgLogo(id: number) {
    return this.delete(`/fsm/organizations/${id}/logo`);
  }

  async deleteOrganization(id: number) {
    return this.delete(`/fsm/organizations/${id}`);
  }


  async listOrgMembers(orgId: number) {
    return this.get(`/fsm/organizations/${orgId}/members`);
  }

  async addOrgMember(
    orgId: number,
    data:
      | { user_id: number; role: string }
      | {
          name: string;
          email: string;
          password: string;
          role: string;
          phone_number?: string;
          country?: string;
          city?: string;
        },
  ) {
    return this.post(`/fsm/organizations/${orgId}/members`, data);
  }

  async updateOrgMemberRole(
    orgId: number,
    userId: number,
    data: {
      role?: string;
      name?: string;
      phone_number?: string;
      country?: string;
      city?: string;
      address?: string;
    },
  ) {
    return this.put(`/fsm/organizations/${orgId}/members/${userId}`, data);
  }

  async removeOrgMember(orgId: number, userId: number) {
    return this.delete(`/fsm/organizations/${orgId}/members/${userId}`);
  }

  async listBrigades(params?: any) {
    return this.get("/fsm/brigades", { params });
  }

  async createBrigade(data: any) {
    return this.post("/fsm/brigades", data);
  }

  async getBrigade(id: number) {
    return this.get(`/fsm/brigades/${id}`);
  }

  async updateBrigade(id: number, data: any) {
    return this.put(`/fsm/brigades/${id}`, data);
  }

  async deleteBrigade(id: number) {
    return this.delete(`/fsm/brigades/${id}`);
  }

  async listWorkers(params?: any) {
    return this.get("/fsm/workers", { params });
  }

  async searchWorkers(query: string, orgId: number) {
    return this.get("/fsm/workers/search", { params: { q: query, org_id: orgId } });
  }

  async createWorkerProfile(data: any) {
    return this.post("/fsm/workers", data);
  }

  async getWorkerProfile(userId: number) {
    return this.get(`/fsm/workers/${userId}`);
  }

  async updateWorkerProfile(userId: number, data: any) {
    return this.put(`/fsm/workers/${userId}`, data);
  }

  async transferWorkerBrigade(userId: number, brigadeId: number) {
    return this.put(`/fsm/workers/${userId}/transfer-brigade`, { brigade_id: brigadeId });
  }

  async createWorkerAccount(data: any) {
    return this.post("/fsm/workers/create-account", data);
  }

  async updateWorkerStatus(userId: number, status: string) {
    return this.put(`/fsm/workers/${userId}/status`, { status });
  }

  async getWorkerStatus(userId: number) {
    return this.get(`/fsm/workers/${userId}/status`);
  }

  async resetWorkerPassword(userId: number, password: string) {
    return this.post(`/fsm/workers/${userId}/reset-password`, { password });
  }

  async archiveWorker(userId: number) {
    return this.put(`/fsm/workers/${userId}/archive`, {});
  }

  async unarchiveWorker(userId: number) {
    return this.put(`/fsm/workers/${userId}/unarchive`, {});
  }

  async listShifts(params?: any) {
    return this.get("/fsm/shifts", { params });
  }

  async createShift(data: any) {
    return this.post("/fsm/shifts", data);
  }

  async getShift(id: number) {
    return this.get(`/fsm/shifts/${id}`);
  }

  async checkinShift(id: number, data: any) {
    return this.post(`/fsm/shifts/${id}/checkin`, data);
  }

  async checkoutShift(id: number, data: any) {
    return this.post(`/fsm/shifts/${id}/checkout`, data);
  }

  async listFSMOrders(params?: any) {
    return this.get("/fsm/orders", { params });
  }

  async getDispatchBoard(orgId: number) {
    return this.get("/fsm/board", { params: { org_id: orgId } });
  }

  async suggestBrigadesForOrder(orderId: number) {
    return this.get("/fsm/orders/suggest-brigades", { params: { order_id: orderId } });
  }

  async getDispatchPulse(orgId: number) {
    return this.get("/fsm/dispatch/pulse", { params: { org_id: orgId } });
  }

  async getBrigadesWorkload(orgId: number, date?: string) {
    return this.get("/fsm/brigades/workload", { params: { org_id: orgId, date } });
  }

  async createFSMOrder(data: any) {
    return this.post("/fsm/orders", data);
  }

  async updateFSMOrder(orderId: number, data: any) {
    return this.put(`/fsm/orders/${orderId}`, data);
  }

  async assignBrigadeToOrder(orderId: number, brigadeId: number) {
    return this.post(`/fsm/orders/${orderId}/assign`, { brigade_id: brigadeId });
  }

  async listOrderParticipants(orderId: number) {
    return this.get(`/fsm/orders/${orderId}/participants`);
  }

  async addOrderParticipant(orderId: number, data: any) {
    return this.post(`/fsm/orders/${orderId}/participants`, data);
  }

  async getFSMOrderDetail(orderId: number) {
    return this.get(`/fsm/orders/${orderId}/detail`);
  }

  async removeParticipant(participantId: number) {
    return this.put(`/fsm/participants/${participantId}/remove`, {});
  }

  async listAssets(params?: any) {
    return this.get("/fsm/assets", { params });
  }

  async getAsset(id: number) {
    return this.get(`/fsm/assets/${id}`);
  }

  async createAsset(data: any) {
    return this.post("/fsm/assets", data);
  }

  async updateAsset(id: number, data: any) {
    return this.put(`/fsm/assets/${id}`, data);
  }

  async getAssetHistory(id: number) {
    return this.get(`/fsm/assets/${id}/history`);
  }

  async assignAssetToBrigade(assetId: number, brigadeId: number) {
    return this.put(`/fsm/assets/${assetId}/assign-brigade`, { brigade_id: brigadeId });
  }

  async unassignAssetFromBrigade(assetId: number) {
    return this.put(`/fsm/assets/${assetId}/unassign-brigade`);
  }

  async assignAsset(data: any) {
    return this.post("/fsm/assets/assign", data);
  }

  async returnAsset(assignmentId: number, data?: any) {
    return this.post(`/fsm/assets/return/${assignmentId}`, data);
  }

  async deleteAsset(id: number) {
    return this.delete(`/fsm/assets/${id}`);
  }

  async getOrderAssets(orderId: number) {
    return this.get(`/fsm/orders/${orderId}/assets`);
  }

  async recordWorkerLocation(data: any) {
    return this.post("/fsm/locations", data);
  }

  async getActiveWorkersLocations(orgId: number) {
    return this.get("/fsm/locations/active", { params: { org_id: orgId } });
  }

  async getFsmMapMarkers(params: {
    org_id: number;
    lat_sw: number;
    lng_sw: number;
    lat_ne: number;
    lng_ne: number;
    zoom: number;
  }) {
    return this.get("/fsm/map-markers", { params });
  }

  async listFeedbacks(params?: any) {
    return this.get("/fsm/feedbacks", { params });
  }

  async createFeedback(data: any) {
    return this.post("/fsm/feedbacks", data);
  }

  // FSM Billing
  async listBillingPlans() {
    return this.get("/fsm/billing/plans");
  }

  async createBillingPlan(data: any) {
    return this.post("/fsm/billing/plans", data);
  }

  async updateBillingPlan(id: number, data: any) {
    return this.put(`/fsm/billing/plans/${id}`, data);
  }

  async previewPricing(orgId: number, planId: number) {
    return this.get("/fsm/billing/preview", { params: { org_id: orgId, plan_id: planId } });
  }

  async getOrgSubscription(orgId: number) {
    return this.get(`/fsm/billing/subscription/${orgId}`);
  }

  async subscribeToPlan(
    orgId: number,
    billingPlanId: number,
    opts?: {
      payment_provider?: string;
      payment_card_id?: number;
      auto_renew?: boolean;
      save_card?: boolean;
      success_url?: string;
      failure_url?: string;
    },
  ) {
    return this.post("/fsm/billing/subscribe", {
      org_id: orgId,
      billing_plan_id: billingPlanId,
      ...(opts?.payment_provider && { payment_provider: opts.payment_provider }),
      ...(opts?.payment_card_id && { payment_card_id: opts.payment_card_id }),
      ...(opts?.auto_renew !== undefined && { auto_renew: opts.auto_renew }),
      ...(opts?.save_card !== undefined && { save_card: opts.save_card }),
      ...(opts?.success_url && { success_url: opts.success_url }),
      ...(opts?.failure_url && { failure_url: opts.failure_url }),
    });
  }

  async setSubscriptionAutopay(id: number, autoRenew: boolean, paymentCardId?: number | null) {
    return this.post(`/fsm/billing/subscription/${id}/autopay`, {
      auto_renew: autoRenew,
      ...(paymentCardId ? { payment_card_id: paymentCardId } : { payment_card_id: null }),
    });
  }

  async cancelSubscription(id: number) {
    return this.post(`/fsm/billing/subscription/${id}/cancel`, {});
  }

  async activateSubscription(id: number) {
    return this.post(`/fsm/billing/subscription/${id}/activate`, {});
  }

  async listInvoices(params?: any) {
    return this.get("/fsm/billing/invoices", { params });
  }

  async payInvoice(
    id: number,
    opts?: {
      payment_provider?: string;
      payment_id?: string;
      payment_card_id?: number;
      auto_renew?: boolean;
      save_card?: boolean;
      success_url?: string;
      failure_url?: string;
    },
  ) {
    return this.post(`/fsm/billing/invoices/${id}/pay`, {
      ...(opts?.payment_provider && { payment_provider: opts.payment_provider }),
      ...(opts?.payment_id && { payment_id: opts.payment_id }),
      ...(opts?.payment_card_id && { payment_card_id: opts.payment_card_id }),
      ...(opts?.auto_renew !== undefined && { auto_renew: opts.auto_renew }),
      ...(opts?.save_card !== undefined && { save_card: opts.save_card }),
      ...(opts?.success_url && { success_url: opts.success_url }),
      ...(opts?.failure_url && { failure_url: opts.failure_url }),
    });
  }

  async getBillingStatus(orgId: number) {
    return this.get(`/fsm/billing/status/${orgId}`);
  }

  async selectOrg(orgId: number) {
    return this.post("/fsm/select-org", { org_id: orgId });
  }

  // FSM Messaging
  async sendMessage(data: {
    org_id: number;
    sender_id: number;
    message_type: string;
    target_type: string;
    target_id?: number;
    title: string;
    body?: string;
    priority?: string;
  }) {
    return this.post("/fsm/messages/send", data);
  }

  async getInbox(userId: number, limit?: number) {
    return this.get("/fsm/messages/inbox", {
      params: { user_id: userId, limit: limit || 50 },
    });
  }

  async getSentMessages(orgId?: number, senderId?: number) {
    return this.get("/fsm/messages/sent", {
      params: { org_id: orgId, sender_id: senderId },
    });
  }

  async markMessageRead(recipientId: number) {
    return this.put(`/fsm/messages/read/${recipientId}`, {});
  }

  async markAllMessagesRead(userId: number) {
    return this.api.put(`/fsm/messages/read-all?user_id=${userId}`, {});
  }

  async checkShiftConflicts(orgId: number, start: string, end: string) {
    return this.get("/fsm/shifts/conflicts", {
      params: { org_id: orgId, start, end },
    });
  }

  async getWorkerAvailability(orgId: number, userId: number, start: string, end: string) {
    return this.get("/fsm/shifts/availability", {
      params: { org_id: orgId, user_id: userId, start, end },
    });
  }

  // ---- Reports ----

  async getOrderManHours(orderId: number) {
    return this.get(`/fsm/reports/orders/${orderId}/man-hours`);
  }

  async getWorkerWorkload(userId: number, start?: string, end?: string) {
    return this.get(`/fsm/reports/workers/${userId}/workload`, {
      params: { start, end },
    });
  }

  async getBrigadeWorkload(brigadeId: number, start?: string, end?: string) {
    return this.get(`/fsm/reports/brigades/${brigadeId}/workload`, {
      params: { start, end },
    });
  }

  async getWorkerKPI(userId: number, start?: string, end?: string) {
    return this.get(`/fsm/reports/workers/${userId}/kpi`, {
      params: { start, end },
    });
  }

  async getBrigadeFinancial(brigadeId: number, start?: string, end?: string) {
    return this.get(`/fsm/reports/brigades/${brigadeId}/financial`, {
      params: { start, end },
    });
  }

  async getOrgSummary(orgId: number, start?: string, end?: string) {
    return this.get(`/fsm/reports/org/${orgId}/summary`, {
      params: { start, end },
    });
  }

  async getOrgTrends(orgId: number, period?: string) {
    return this.get(`/fsm/reports/org/${orgId}/trends`, {
      params: { period },
    });
  }

  async getOrderStatusDistribution(orgId: number) {
    return this.get(`/fsm/reports/orders/status-distribution`, {
      params: { org_id: orgId },
    });
  }

  async getOrderCategoryBreakdown(orgId: number, lang?: string) {
    return this.get(`/fsm/reports/orders/category-breakdown`, {
      params: { org_id: orgId, ...(lang ? { lang } : {}) },
    });
  }

  // ---- Brigade Senior ----

  async setBrigadeSenior(brigadeId: number, data: { senior_worker_id: number; seniority_type?: string }) {
    return this.post(`/fsm/brigades/${brigadeId}/senior`, data);
  }

  async removeBrigadeSenior(brigadeId: number) {
    return this.delete(`/fsm/brigades/${brigadeId}/senior`);
  }

  // ---- Permissions & Grants (RBAC) ----

  async listRoles() {
    return this.get("/fsm/roles");
  }

  async listPermissions() {
    return this.get("/fsm/permissions");
  }

  async createRole(data: { name: string; slug: string; description: string; is_system: boolean; permissions: string[] }) {
    return this.post("/fsm/roles", data);
  }

  async updateRole(id: number, data: { name?: string; description?: string; permissions?: string[] }) {
    return this.put(`/fsm/roles/${id}`, data);
  }

  async deleteRole(id: number) {
    return this.delete(`/fsm/roles/${id}`);
  }

  async getRole(slug: string) {
    return this.get(`/fsm/roles/${slug}`);
  }

  async listGrants(params?: { user_id?: number; scope_type?: string; scope_id?: number }) {
    return this.get("/fsm/grants", { params });
  }

  async myGrants() {
    return this.get("/fsm/grants/my");
  }

  async grantRole(data: { user_id: number; role: string; scope_type: string; scope_id: number }) {
    return this.post("/fsm/grants/grant", data);
  }

  async revokeGrant(grantId: number) {
    return this.delete(`/fsm/grants/${grantId}`);
  }

  async checkPermission(resource: string, action: string, scopeType: string, scopeId: number) {
    return this.get("/fsm/grants/check", { params: { resource, action, scope_type: scopeType, scope_id: scopeId } });
  }

  async listBrigadeMembers(brigadeId: number) {
    return this.get(`/fsm/brigades/${brigadeId}/members`);
  }

  // ---- Order Photos (FSM) ----

  async uploadFSMOrderPhoto(orderId: number, formData: FormData) {
    return this.post(`/fsm/orders/${orderId}/photos`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  async listOrderPhotos(orderId: number, type?: string) {
    return this.get(`/fsm/orders/${orderId}/photos`, { params: type ? { type } : {} });
  }

  async updateOrderPhoto(photoId: number, data: Partial<{ comment: string; type: string; sort_order: number }>) {
    return this.put(`/fsm/photos/${photoId}`, data);
  }

  async deleteOrderPhoto(photoId: number) {
    return this.delete(`/fsm/photos/${photoId}`);
  }

  // ---- Photo Comments ----

  async createPhotoComment(photoId: number, data: { content: string; parent_id?: number }) {
    return this.post(`/fsm/photos/${photoId}/comments`, data);
  }

  async listPhotoComments(photoId: number) {
    return this.get(`/fsm/photos/${photoId}/comments`);
  }

  async updatePhotoComment(commentId: number, data: { content: string }) {
    return this.put(`/fsm/photos/comments/${commentId}`, data);
  }

  async deletePhotoComment(commentId: number) {
    return this.delete(`/fsm/photos/comments/${commentId}`);
  }

  // ---- Work Timers ----

  async startWorkTimer(orderId: number) {
    return this.post(`/fsm/orders/${orderId}/timers/start`, {});
  }

  async getActiveWorkTimer(orderId: number, userId?: number) {
    return this.get(`/fsm/orders/${orderId}/timers/active`, { params: userId ? { user_id: userId } : {} });
  }

  async listWorkTimers(orderId: number, params?: { status?: string; user_id?: number }) {
    return this.get(`/fsm/orders/${orderId}/timers`, { params });
  }

  async pauseWorkTimer(timerId: number) {
    return this.put(`/fsm/timers/${timerId}/pause`, {});
  }

  async resumeWorkTimer(timerId: number) {
    return this.put(`/fsm/timers/${timerId}/resume`, {});
  }

  async completeWorkTimer(timerId: number) {
    return this.put(`/fsm/timers/${timerId}/complete`, {});
  }

  // ---- Field Assignments (выездные наряды) ----

  async listFieldAssignments(orderId: number) {
    return this.get(`/fsm/orders/${orderId}/assignments`);
  }

  async listBrigadeFieldAssignments(brigadeId: number) {
    return this.get(`/fsm/brigades/${brigadeId}/assignments`);
  }

  async createFieldAssignment(orderId: number, data: any) {
    return this.post(`/fsm/orders/${orderId}/assignments`, data);
  }

  async getFieldAssignment(id: number) {
    return this.get(`/fsm/assignments/${id}`);
  }

  async updateFieldAssignment(id: number, data: any) {
    return this.put(`/fsm/assignments/${id}`, data);
  }

  async deleteFieldAssignment(id: number) {
    return this.delete(`/fsm/assignments/${id}`);
  }

  async updateFSMOrderStatus(orderId: number, status: string) {
    return this.put(`/fsm/orders/${orderId}/status`, { status });
  }

  async createStage(assignmentId: number, data: any) {
    return this.post(`/fsm/assignments/${assignmentId}/stages`, data);
  }

  async updateStage(stageId: number, data: any) {
    return this.put(`/fsm/stages/${stageId}`, data);
  }

  async deleteStage(stageId: number) {
    return this.delete(`/fsm/stages/${stageId}`);
  }

  getFsmWsUrl(orgId: number, token?: string): string {
    const wsBaseUrl = import.meta.env.VITE_WS_BASE_URL;
    const authToken = token || localStorage.getItem('token') || '';
    const isHttps = window.location.protocol === 'https:';
    const base = wsBaseUrl || `${isHttps ? 'wss:' : 'ws:'}//${window.location.host}`;
    const encodedToken = encodeURIComponent(authToken);
    return `${base}/api/v1/fsm/ws/tracking?token=${encodedToken}&org_id=${orgId}`;
  }

  // ---- Chats ----

  async listChats(params?: { org_id?: number; type?: string; search?: string; date_from?: string; date_to?: string }) {
    return this.get("/fsm/chats", { params: params || {} });
  }

  async getOrCreateChat(data: { type: string; ref_id?: number | null; org_id: number; user_ids?: number[] }) {
    return this.post("/fsm/chats", data);
  }

  async getChatMessages(chatId: number, limit?: number) {
    return this.get(`/fsm/chats/${chatId}/messages`, { params: limit ? { limit } : {} });
  }

  async sendChatMessage(chatId: number, formData: FormData) {
    const response = await this.api.post(`/fsm/chats/${chatId}/messages`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return { error: null, data: response.data.data };
  }

  async markChatRead(chatId: number) {
    return this.post(`/fsm/chats/${chatId}/read`);
  }
}

const api = new ApiService();
export default api;

